import FeatureGates from '@atlaskit/feature-gate-js-client';
import { ApplicationData } from '../../../../utilities/applications';
import { AnalyticsClient } from '../../../../utilities/analytics/analytics-web-client';
import { checkFeatureGate } from '../../../../utilities/feature-flags/feature-gate';
import { AidFrontendFeatureGate } from '../../../../providers/FeatureGates';
import { SimpleCobranding } from '../../../../utilities/cobranding/cobranding';

/** Logo appearance values for AtlassianLogo - matches @atlaskit/logo's Appearance type. */
export type LogoAppearanceType = 'neutral' | 'inverse' | 'brand';

const TWCG_786_EXPERIMENT_KEY = 'twcg-786-prioritise-sso-on-login-page';

/** Guard: fire exposure event at most once per page load (resets on full reload). */
let twcg786ExposureEventFired = false;

export type TWCG786ExperimentConfig = {
  simpleCobranding: SimpleCobranding;
  textColor: string;
  textColorMobile?: string;
  logoVersion: LogoAppearanceType;
  logoVersionMobile?: LogoAppearanceType;
};

/**
 * The resolved config for the active experiment session.
 * Cleared at the start of checkAndActivateTWCG786Experiment() on each cobranding
 * resolution, then set by activateTWCG786ExperimentForApplication() if the user is
 * in the variation cohort.
 */
let activeExperimentConfig: TWCG786ExperimentConfig | undefined;

export const getActiveExperimentConfig = (): TWCG786ExperimentConfig | undefined =>
  activeExperimentConfig;

/**
 * Per-application config for the TWCG-786 experiment.
 * Add an entry here when onboarding a new application to the experiment.
 *
 * Defined as a function to avoid a circular-dependency initialisation issue:
 * experiment-utils.ts and cobranding.tsx import each other, so referencing
 * SimpleCobranding at module scope would resolve before cobranding.tsx finishes
 * evaluating. Wrapping it in a function defers the access to call time.
 */
const getConfigByApplication = (): Partial<Record<string, TWCG786ExperimentConfig>> => ({
  admin: {
    simpleCobranding: SimpleCobranding.NO_COBRANDING,
    textColor: 'color.text.subtle',
    textColorMobile: 'color.text.subtle',
    logoVersion: 'neutral',
    logoVersionMobile: 'neutral',
  },
  atlas: {
    simpleCobranding: SimpleCobranding.ATLAS,
    textColor: 'color.text.subtle',
    textColorMobile: 'color.text.subtle',
    logoVersion: 'neutral',
    logoVersionMobile: 'neutral',
  },
  bitbucket: {
    simpleCobranding: SimpleCobranding.BITBUCKET,
    textColor: 'color.text.inverse',
    textColorMobile: 'color.text.subtle',
    logoVersion: 'inverse',
    logoVersionMobile: 'neutral',
  },
  community: {
    simpleCobranding: SimpleCobranding.COMMUNITY,
    textColor: 'color.text.subtle',
    textColorMobile: 'color.text.subtle',
    logoVersion: 'neutral',
    logoVersionMobile: 'neutral',
  },
  confluence: {
    simpleCobranding: SimpleCobranding.CONFLUENCE,
    textColor: 'color.text.inverse',
    textColorMobile: 'color.text.subtle',
    logoVersion: 'inverse',
    logoVersionMobile: 'neutral',
  },
  jira: {
    simpleCobranding: SimpleCobranding.JIRA,
    textColor: 'color.text.inverse',
    textColorMobile: 'color.text.subtle',
    logoVersion: 'inverse',
    logoVersionMobile: 'neutral',
  },
  'jira-align': {
    simpleCobranding: SimpleCobranding.JIRA_ALIGN,
    textColor: 'color.text.subtle',
    textColorMobile: 'color.text.subtle',
    logoVersion: 'neutral',
    logoVersionMobile: 'neutral',
  },
  loom: {
    simpleCobranding: SimpleCobranding.LOOM,
    textColor: 'color.text.subtle',
    textColorMobile: 'color.text.subtle',
    logoVersion: 'neutral',
    logoVersionMobile: 'neutral',
  },
  mpac: {
    simpleCobranding: SimpleCobranding.NO_COBRANDING,
    textColor: 'color.text.subtle',
    textColorMobile: 'color.text.subtle',
    logoVersion: 'neutral',
    logoVersionMobile: 'neutral',
  },
  opsgenie: {
    simpleCobranding: SimpleCobranding.OPSGENIE,
    textColor: 'color.text.subtle',
    textColorMobile: 'color.text.subtle',
    logoVersion: 'neutral',
    logoVersionMobile: 'neutral',
  },
  prt: {
    simpleCobranding: SimpleCobranding.PRT,
    textColor: 'color.text.subtle',
    textColorMobile: 'color.text.subtle',
    logoVersion: 'neutral',
    logoVersionMobile: 'neutral',
  },
  rovo: {
    simpleCobranding: SimpleCobranding.ROVO,
    textColor: 'color.text.inverse',
    textColorMobile: 'color.text.subtle',
    logoVersion: 'inverse',
    logoVersionMobile: 'neutral',
  },
  sac: {
    simpleCobranding: SimpleCobranding.NO_COBRANDING,
    textColor: 'color.text.subtle',
    textColorMobile: 'color.text.subtle',
    logoVersion: 'neutral',
    logoVersionMobile: 'neutral',
  },
  statuspage: {
    simpleCobranding: SimpleCobranding.STATUSPAGE,
    textColor: 'color.text.subtle',
    textColorMobile: 'color.text.subtle',
    logoVersion: 'neutral',
    logoVersionMobile: 'neutral',
  },
  support: {
    simpleCobranding: SimpleCobranding.NO_COBRANDING,
    textColor: 'color.text.subtle',
    textColorMobile: 'color.text.subtle',
    logoVersion: 'neutral',
    logoVersionMobile: 'neutral',
  },
  trello: {
    simpleCobranding: SimpleCobranding.TRELLO,
    textColor: 'color.text.subtle',
    textColorMobile: 'color.text.subtle',
    logoVersion: 'neutral',
    logoVersionMobile: 'neutral',
  },
  wac: {
    simpleCobranding: SimpleCobranding.NO_COBRANDING,
    textColor: 'color.text.subtle',
    textColorMobile: 'color.text.subtle',
    logoVersion: 'neutral',
    logoVersionMobile: 'neutral',
  },
});

/**
 * Single call site for the experiment: checks eligibility via the config map,
 * evaluates the gate/cohort, and activates the config if the user is in variation.
 *
 * When applicationData is null/undefined (unknown application), the experiment
 * still runs using a default NO_COBRANDING config.
 *
 * Defined as a function (rather than a module-scope constant) to avoid the
 * circular-dependency initialisation issue between experiment-utils.ts and
 * cobranding.tsx — SimpleCobranding must be accessed at call time, not at
 * module evaluation time.
 *
 * To add a new application to the experiment:
 * 1. Add an entry in getConfigByApplication()
 * 2. Add a corresponding case in cobranding.tsx that calls this function
 *
 * Returns true if the caller should route to TWCG_786_PRIORITISE_SSO_ON_LOGIN_PAGE.
 */
export function checkAndActivateTWCG786Experiment(
  applicationData: ApplicationData | undefined | null,
  analyticsClient: AnalyticsClient | undefined
): boolean {
  activeExperimentConfig = undefined;

  const defaultConfig: TWCG786ExperimentConfig = {
    simpleCobranding: SimpleCobranding.NO_COBRANDING,
    textColor: 'color.text.subtle',
    textColorMobile: 'color.text.subtle',
    logoVersion: 'neutral',
    logoVersionMobile: 'neutral',
  };

  const config = applicationData
    ? getConfigByApplication()[applicationData.coreApplication]
    : defaultConfig;

  if (!config) {
    return false;
  }

  if (isTWCG786EnabledAndVariationCohort(applicationData, analyticsClient)) {
    activeExperimentConfig = config;
    return true;
  }
  return false;
}

/** Pure gate/cohort check. Does not mutate module state. */
export function isTWCG786EnabledAndVariationCohort(
  applicationData: ApplicationData | undefined | null,
  analyticsClient: AnalyticsClient | undefined
): boolean {
  try {
    const twcg768GateEnabled = checkFeatureGate(
      AidFrontendFeatureGate.TWCG_786_PRIORITISE_SSO_ON_LOGIN_PAGE_GATE
    );

    if (!twcg768GateEnabled) {
      return false;
    }

    const cohortValue = FeatureGates.getExperimentValue(
      TWCG_786_EXPERIMENT_KEY,
      'cohort',
      'not-enrolled'
    ) as 'variation' | 'control' | 'not-enrolled';

    if (analyticsClient && !twcg786ExposureEventFired) {
      twcg786ExposureEventFired = true;
      analyticsClient.trackingEvent({
        page: 'login-page',
        action: 'exposed',
        subject: TWCG_786_EXPERIMENT_KEY,
        attributes: {
          flagKey: TWCG_786_EXPERIMENT_KEY,
          group: cohortValue,
          application: applicationData?.coreApplication,
          experience: applicationData?.experience,
        },
      });
    }

    return twcg768GateEnabled && cohortValue === 'variation';
  } catch (error) {
    analyticsClient?.operationalEvent({
      page: 'login-page',
      action: 'failed',
      subject: TWCG_786_EXPERIMENT_KEY,
      attributes: {
        flagKey: TWCG_786_EXPERIMENT_KEY,
        application: applicationData?.coreApplication,
        experience: applicationData?.experience,
        errorName: error?.name,
        errorStack: error?.stack,
        errorMessage: error?.message,
        errorCode: error?.code,
      },
    });
    return false;
  }
}
